import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Loader2, RefreshCw, Check, X, Truck, Send } from "lucide-react";

const fmtDate = (v) => {
  if (!v) return null;
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return String(v);
    return d.toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return String(v);
  }
};

const statusStyle = (s) => {
  const v = String(s || "").toLowerCase();
  if (v.includes("award") || v.includes("accept")) return "bg-emerald-100 text-emerald-700 border-emerald-300";
  if (v.includes("reject") || v.includes("declin")) return "bg-red-100 text-red-700 border-red-300";
  if (v.includes("counter")) return "bg-amber-100 text-amber-700 border-amber-300";
  return "bg-slate-100 text-slate-600 border-slate-300";
};

/**
 * Inline panel that lists incoming Transporeon carrier spot quotes for a
 * published offer (transportNumber) with one-click Award / Reject.
 * Shared by the Position Folder spot modal and the Route Calculator.
 */
export const TransporeonQuotesPanel = ({ apiUrl, token, transportNumber, onConvert, convertingId, autoLoad = true }) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actioning, setActioning] = useState(null);
  const [acted, setActed] = useState({}); // offer_id -> "awarded" | "rejected"
  const [loadedOnce, setLoadedOnce] = useState(false);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchOffers = useCallback(async (silent = false) => {
    if (!transportNumber) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${apiUrl}/transporeon/${transportNumber}/offers`, authHeaders);
      setOffers(data.offers || []);
      if (!silent && !(data.offers || []).length) toast.info("No carrier quotes yet — check back shortly");
    } catch (error) {
      if (!silent) toast.error(error.response?.data?.detail || "Failed to pull quotes");
    } finally {
      setLoading(false);
      setLoadedOnce(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, token, transportNumber]);

  useEffect(() => {
    if (autoLoad && transportNumber) fetchOffers(true);
  }, [autoLoad, transportNumber, fetchOffers]);

  const doAction = async (offer, kind) => {
    const oid = offer.offer_id || offer.carrier_creditor_number;
    setActioning(oid);
    try {
      const url = kind === "award" ? `${apiUrl}/transporeon/accept-offer` : `${apiUrl}/transporeon/reject-offer`;
      await axios.post(url, {
        transport_number: transportNumber,
        offer_uid: offer.offer_id,
        carrier_creditor_number: offer.carrier_creditor_number,
      }, authHeaders);
      setActed((p) => ({ ...p, [oid]: kind === "award" ? "awarded" : "rejected" }));
      toast.success(kind === "award" ? "Quote awarded to carrier" : "Quote rejected");
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${kind} quote`);
    } finally {
      setActioning(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="transporeon-quotes-panel">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">
          Reference: <span className="font-mono text-slate-800">{transportNumber}</span>
        </span>
        <Button size="sm" variant="outline" onClick={() => fetchOffers(false)} disabled={loading} data-testid="transporeon-refresh-quotes-btn">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh quotes
        </Button>
      </div>

      {(!offers || offers.length === 0) ? (
        <div className="text-center py-8 text-muted-foreground text-sm border rounded-lg bg-slate-50">
          {loading ? "Loading quotes…" : (loadedOnce
            ? "Published to carriers. No quotes have arrived yet — click Refresh quotes."
            : "Click Refresh quotes to fetch carrier responses.")}
        </div>
      ) : (
        <div className="space-y-2" data-testid="transporeon-quotes-list">
          {offers.map((o, i) => {
            const oid = o.offer_id || o.carrier_creditor_number;
            const done = acted[oid];
            return (
              <div key={oid || i} className="flex items-center justify-between border rounded-lg p-3" data-testid={`transporeon-quote-${i}`}>
                <div className="min-w-0">
                  <div className="font-medium text-slate-800 flex items-center gap-2">
                    <Truck className="h-4 w-4 text-indigo-600 shrink-0" />
                    <span className="truncate">{o.carrier_name || o.carrier_creditor_number || "Carrier"}</span>
                    {done && <Badge className={`text-[10px] border ${statusStyle(done)}`}>{done}</Badge>}
                    {!done && o.qualifier && <Badge className={`text-[10px] border ${statusStyle(o.qualifier)}`}>{o.qualifier}</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {o.price != null && o.price !== ""
                      ? <span className="font-semibold text-slate-700" data-testid={`transporeon-quote-price-${i}`}>{o.price} {o.currency || ""}</span>
                      : <span className="italic">No price</span>}
                    {o.binding_until ? <span className="ml-2">· valid until {fmtDate(o.binding_until)}</span> : null}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="sm"
                    onClick={() => doAction(o, "award")}
                    disabled={!!actioning || !!done}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`transporeon-award-${i}`}
                    title="Award this quote to the carrier"
                  >
                    {actioning === oid ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
                    Award
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => doAction(o, "reject")}
                    disabled={!!actioning || !!done}
                    className="border-red-300 text-red-700 hover:bg-red-50"
                    data-testid={`transporeon-reject-${i}`}
                    title="Reject this quote"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Reject
                  </Button>
                  {onConvert && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onConvert(o)}
                      disabled={convertingId === oid}
                      data-testid={`transporeon-convert-${i}`}
                      title="Create a Transport Order from this quote"
                    >
                      {convertingId === oid ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                      Create TO
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TransporeonQuotesPanel;
